import { useCallback } from "react";
import { useApi } from "../callApi/callApi";
import {
  WeekScoresResponse,
  YearScoresResponse,
  AvailableYearsAndWeeksResponse,
} from "../../features/cfbd/types/score";

/**
 * Custom hook for scores operations
 */
export const useScores = () => {
  const callApi = useApi();

  const getAvailableYearsAndWeeks = useCallback(
    async (
      leagueId: string
    ): Promise<AvailableYearsAndWeeksResponse | Error> => {
      try {
        const response = await callApi(
          `leagues/${leagueId}/scores`,
          {
            method: "GET",
          },
          ""
        );
        if (!response.ok)
          throw new Error("Failed to fetch available years and weeks");
        const data: AvailableYearsAndWeeksResponse = await response.json();
        return data;
      } catch (err: any) {
        return err instanceof Error ? err : new Error("Unknown error");
      }
    },
    [callApi]
  );

  const getYearScores = useCallback(
    async (
      leagueId: string,
      year: number
    ): Promise<YearScoresResponse | Error> => {
      try {
        const response = await callApi(
          `leagues/${leagueId}/scores/${year}`,
          {
            method: "GET",
          },
          ""
        );
        if (!response.ok) throw new Error("Failed to fetch year scores");
        const data: YearScoresResponse = await response.json();
        return data;
      } catch (err: any) {
        return err instanceof Error ? err : new Error("Unknown error");
      }
    },
    [callApi]
  );

  const getWeekScores = useCallback(
    async (
      leagueId: string,
      year: number,
      week: number
    ): Promise<WeekScoresResponse | Error> => {
      try {
        const response = await callApi(
          `leagues/${leagueId}/scores/${year}/${week}`,
          {
            method: "GET",
          },
          ""
        );
        if (!response.ok) throw new Error("Failed to fetch week scores");
        const data: WeekScoresResponse = await response.json();
        return data;
      } catch (err: any) {
        return err instanceof Error ? err : new Error("Unknown error");
      }
    },
    [callApi]
  );

  return {
    getAvailableYearsAndWeeks,
    getYearScores,
    getWeekScores,
  };
};

export default useScores;
